import pluralize from 'pluralize';
import { SOLR_DELAY } from '../../../constants';
import errorMessage from '../errorMessage';
import { registerDevice } from '../../actions/deviceActions';

export const unregisterCallbackHelper = ({
  results,
  getSelectedItems,
  addNotification,
  unselectAll,
  getDevices,
}) => {
  const items = getSelectedItems();
  let failedItems;
  let errorMsg = '';

  // check for errors
  if (items.length === results.length) {
    addNotification({
      type: 'success',
      text: `${pluralize('Device', items.length, true)} unregistered.`,
    });
  } else {
    // collect the failed request items
    failedItems = items.filter(item => !results.includes(item));
    failedItems.forEach(item => {
      errorMsg += (errorMsg ? ', ' : 'Device unregister request failed: ') + item.description;
    });
    addNotification({ type: 'danger', text: errorMsg });
  }

  // if there was at least one successful request, update
  if (results.length) {
    unselectAll();
    setTimeout(getDevices, SOLR_DELAY);
  }
};

export const rebootCallbackHelper = ({ results, addNotification, getSelectedItems }) => {
  const items = getSelectedItems();
  let failedItems;
  let errorMsg = '';

  // check for errors
  if (items.length === results.length) {
    addNotification({
      type: 'success',
      text: `${pluralize('Device reboot', items.length, true)} requested.`,
    });
  } else {
    // collect the failed request items
    failedItems = items.filter(item => !results.includes(item));
    failedItems.forEach(item => {
      errorMsg += (errorMsg ? ', ' : 'Device reboot request failed: ') + item.description;
    });
    addNotification({ type: 'danger', text: errorMsg });
  }
};

export const restartCallbackHelper = ({ results, addNotification, getSelectedItems }) => {
  const items = getSelectedItems();
  let failedItems;
  let errorMsg = '';

  if (items.length === results.length) {
    addNotification({
      type: 'success',
      text: `${pluralize('Player restart', items.length, true)} requested.`,
    });
  } else {
    // collect the failed request items
    failedItems = items.filter(item => !results.includes(item));
    failedItems.forEach(item => {
      errorMsg += (errorMsg ? ', ' : 'Player restart request failed: ') + item.description;
    });
    addNotification({ type: 'danger', text: errorMsg });
  }
};

export const clearDataCallbackHelper = ({
  results,
  getSelectedItems,
  addNotification,
  unselectAll,
  getDevices,
}) => {
  const items = getSelectedItems();
  let failedItems;
  let errorMsg = '';

  // check for errors
  if (items.length === results.length) {
    addNotification({
      type: 'success',
      text: `${pluralize('Clear signage data', items.length, true)} requested.`,
    });
  } else {
    // collect the failed request items
    failedItems = items.filter(item => !results.includes(item));
    failedItems.forEach(item => {
      errorMsg += (errorMsg ? ', ' : 'Clear signage data request failed: ') + item.description;
    });
    addNotification({ type: 'danger', text: errorMsg });
  }

  // if there was at least one successful request, update
  if (results.length) {
    unselectAll();
    setTimeout(getDevices, SOLR_DELAY);
  }
};

export const upgradeCallbackHelper = ({ results, addNotification, getSelectedItems }) => {
  const items = getSelectedItems();
  let failedItems;
  let errorMsg = '';

  // check for errors
  if (items.length === results.length) {
    addNotification({
      type: 'success',
      text: `${pluralize('Device upgrade', items.length, true)} requested.`,
    });
  } else {
    // collect the failed request items
    failedItems = items.filter(item => !results.includes(item));
    failedItems.forEach(item => {
      errorMsg += (errorMsg ? ', ' : 'Device upgrade request failed: ') + item.description;
    });
    addNotification({ type: 'danger', text: errorMsg });
  }
};

export const registerHelper = ({
  id,
  description,
  history,
  addNotification,
  setShowRegisterDialog,
}) => {
  return registerDevice(id, description).then(
    res => {
      addNotification({ type: 'success', text: 'Device registered.' });
      setShowRegisterDialog(false);
      history.push(`/devices/${res.id}?edit`);
    },
    err =>
      addNotification({
        type: 'danger',
        text: `Registration failed. Please check that the device ID is correct. ${errorMessage(
          err,
        )}`,
      }),
  );
};

export const mergeDevicesStatusHelper = ({
  nextProps,
  setNumFound,
  getHeartbeatPercent,
  setStatusAndDevices,
}) => {
  const {
    devices: {
      devices: { devices, numFound },
      status: { statuses },
    },
  } = nextProps;

  setNumFound(numFound);
  const mergedDevices = [];

  if (!devices) {
    return;
  }

  devices.forEach(device => {
    const status = (statuses || []).find(item => device.id === item.id);
    const mergedDevice = { ...device, ...status };

    mergedDevice.heartbeatPercent = getHeartbeatPercent(mergedDevice);
    mergedDevices.push(mergedDevice);
  });

  setStatusAndDevices(mergedDevices);
};

export const getUptimeButton = device => {
  const { heartbeatPercent } = device;
  const thresholdOffline = 50;
  const thresholdIdle = 90;
  let image = '/assets/images/icons/red_network_layer_icon.svg';

  if (!device.idle) {
    image = '/assets/images/icons/orange_network_layer_icon.svg';
  } else if (heartbeatPercent >= thresholdIdle) {
    image = '/assets/images/icons/green_network_layer_icon.svg';
  } else if (heartbeatPercent >= thresholdOffline) {
    image = '/assets/images/icons/amber_network_layer_icon.svg';
  }

  return image;
};

export const diagnosticsCallbackHelper = ({
  results,
  getSelectedItems,
  addNotification,
  localGetStatus,
}) => {
  const items = getSelectedItems();
  let failedItems;
  let errorMsg = '';

  if (items.length === results.length) {
    addNotification({
      type: 'success',
      text: `${pluralize('Device diagnostics', items.length, true)} requested.`,
    });
  } else {
    // collect the failed request items
    failedItems = items.filter(item => !results.includes(item));
    failedItems.forEach(item => {
      errorMsg += (errorMsg ? ', ' : 'Device diagnostics request failed: ') + item.description;
    });
    addNotification({ type: 'danger', text: errorMsg });
  }

  localGetStatus();
};

export const copyConfigCallbackHelper = ({
  results,
  getSelectedExceptCopied,
  addNotification,
  unselectAll,
  getDevices,
}) => {
  const items = getSelectedExceptCopied();
  let failedItems;
  let errorMsg = '';

  if (items.length === results.length) {
    addNotification({
      type: 'success',
      text: `${pluralize('Device', items.length, true)} settings updated.`,
    });
  } else {
    // collect the failed request items
    failedItems = items.filter(item => !results.includes(item));
    failedItems.forEach(item => {
      errorMsg += (errorMsg ? ', ' : 'Device settings update failed: ') + item.description;
    });
    addNotification({ type: 'danger', text: errorMsg });
  }

  // if there was at least one successful request, update
  if (results.length) {
    unselectAll();
    setTimeout(getDevices, SOLR_DELAY);
  }
};

export const screenshotCallbackHelper = ({
  results,
  addNotification,
  localGetStatus,
  getSelectedItems,
}) => {
  const items = getSelectedItems();
  let failedItems;
  let errorMsg = '';

  if (items.length === results.length) {
    addNotification({
      type: 'success',
      text: `${pluralize('Device screenshot', items.length, true)} requested.`,
    });
  } else {
    // collect the failed request items
    failedItems = items.filter(item => !results.includes(item));
    failedItems.forEach(item => {
      errorMsg += (errorMsg ? ', ' : 'Device screenshot request failed: ') + item.description;
    });
    addNotification({ type: 'danger', text: errorMsg });
  }

  localGetStatus();
};
